import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatMoney, todayISO } from "../lib/api";
import { Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable line-item entry form for purchase / sale / transfer / adjustment.
 * props: type = "purchase" | "sale" | "transfer" | "adjustment" | "defective"
 */
export default function EntryForm({ type, onSuccess }) {
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [parties, setParties] = useState([]);

  const isPurchase = type === "purchase";
  const isSale = type === "sale";
  const isTransfer = type === "transfer";
  const isAdjust = type === "adjustment" || type === "defective";
  const needsParty = isPurchase || isSale;
  const hasMoney = isPurchase || isSale;

  const [form, setForm] = useState({
    date: todayISO(),
    challan_no: "",
    challan_series: "",
    party_id: "",
    from_godown: "",
    to_godown: "",
    godown: "",
    driver_name: "",
    vehicle_no: "",
    payment_received: 0,
    other_charges: 0,
    other_charges_note: "",
    notes: "",
    mt_pending: false,
  });
  const [items, setItems] = useState([emptyItem()]);
  const [busy, setBusy] = useState(false);

  function emptyItem() {
    return { brand: "", size: "", filled_qty: 0, mt_qty: 0, rate: 0, amount: 0, direction: "add" };
  }

  // Rate is ₹/kg for both sale and purchase → amount = filled_qty × size_kg × rate
  function sizeKg(sizeName) {
    const m = String(sizeName || "").toLowerCase().replace("kg", "").trim();
    const n = parseFloat(m);
    return isNaN(n) ? 0 : n;
  }
  function computeAmount(it) {
    const fq = Number(it.filled_qty || 0);
    const rate = Number(it.rate || 0);
    if (isSale || isPurchase) {
      return +(fq * sizeKg(it.size) * rate).toFixed(2);
    }
    return +(fq * rate).toFixed(2);
  }

  useEffect(() => {
    (async () => {
      const [b, s, g, p] = await Promise.all([
        api.get("/brands"), api.get("/sizes"), api.get("/godowns"),
        needsParty ? api.get(`/parties?type=${isPurchase ? "vendor" : "customer"}`) : Promise.resolve({ data: [] }),
      ]);
      setBrands(b.data); setSizes(s.data); setGodowns(g.data); setParties(p.data);
      // defaults
      setForm((f) => ({
        ...f,
        from_godown: g.data[0]?.name || "",
        to_godown: g.data[1]?.name || "",
        godown: g.data[0]?.name || "",
      }));
    })();
  }, [type]);

  const itemsTotal = useMemo(
    () => items.reduce((s, it) => s + Number(it.amount || 0), 0),
    [items]
  );
  const otherCharges = Number(form.other_charges || 0);
  const total = itemsTotal + otherCharges;

  const updateItem = async (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    const it = next[idx];
    // recalc amount when qty, rate, or size changes (size matters for sale)
    if (patch.filled_qty !== undefined || patch.rate !== undefined || patch.size !== undefined) {
      it.amount = computeAmount(it);
    }
    setItems([...next]);

    // last price suggestion on sale when brand+size selected and party known
    if ((isSale || isPurchase) && form.party_id && it.brand && it.size && (patch.brand || patch.size) && !it.rate) {
      try {
        const { data } = await api.get(
          `/last-price?party_id=${form.party_id}&brand=${encodeURIComponent(it.brand)}&size=${encodeURIComponent(it.size)}`
        );
        if (data.rate > 0) {
          const copy = [...next];
          copy[idx] = { ...copy[idx], rate: data.rate };
          copy[idx].amount = computeAmount(copy[idx]);
          setItems(copy);
          toast.info(`Last rate ₹${formatMoney(data.rate)}${(isSale || isPurchase) ? "/kg" : ""} applied (editable)`);
        }
      } catch {}
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        type: type === "defective" ? "defective" : (isAdjust ? "adjustment" : type),
        date: form.date,
        challan_no: form.challan_no || "",
        challan_series: form.challan_series || "",
        party_id: needsParty ? form.party_id : null,
        from_godown: isTransfer ? form.from_godown : (needsParty ? form.from_godown : null),
        to_godown: isTransfer ? form.to_godown : null,
        godown: isAdjust ? form.godown : null,
        driver_name: form.driver_name,
        vehicle_no: form.vehicle_no,
        items: items.filter((it) => it.brand && it.size && (it.filled_qty || it.mt_qty || (isSale && form.mt_pending && it.filled_qty)))
          .map((it) => {
            if (type !== "adjustment") return it;
            const sign = it.direction === "reduce" ? -1 : 1;
            return {
              ...it,
              filled_qty: sign * Math.abs(Number(it.filled_qty || 0)),
              mt_qty: sign * Math.abs(Number(it.mt_qty || 0)),
            };
          }),
        total_amount: hasMoney ? total : 0,
        other_charges: hasMoney ? otherCharges : 0,
        other_charges_note: hasMoney ? (form.other_charges_note || "") : "",
        payment_received: hasMoney ? Number(form.payment_received || 0) : 0,
        notes: form.notes,
        mt_pending: isSale ? !!form.mt_pending : false,
      };
      if (payload.mt_pending) {
        // zero MT on all items when dispatching with MT pending
        payload.items = payload.items.map((it) => ({ ...it, mt_qty: 0 }));
      }
      if (type === "defective") {
        payload.type = "defective";
      }
      if (payload.items.length === 0) {
        // Allow charges-only entries for sale/purchase
        if (hasMoney && otherCharges > 0) {
          // proceed
        } else {
          toast.error("Add at least one cylinder line, or set Other Charges");
          setBusy(false); return;
        }
      }
      await api.post("/transactions", payload);
      toast.success(payload.mt_pending
        ? "Sale dispatched · MT entry pending"
        : `${type.charAt(0).toUpperCase() + type.slice(1)} saved`);
      setForm({
        date: todayISO(), challan_no: "", challan_series: "",
        party_id: form.party_id, from_godown: form.from_godown,
        to_godown: form.to_godown, godown: form.godown,
        driver_name: "", vehicle_no: "", payment_received: 0,
        other_charges: 0, other_charges_note: "",
        notes: "", mt_pending: false,
      });
      setItems([emptyItem()]);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={`entry-form-${type}`}>
      {/* Header row */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="small-label block mb-2">Date</label>
          <input type="date" className="input-lg" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="entry-date" required />
        </div>
        <div>
          <label className="small-label block mb-2">Challan No</label>
          <input className="input-lg" value={form.challan_no}
            onChange={(e) => setForm({ ...form, challan_no: e.target.value })}
            placeholder="e.g. AMB-102"
            data-testid="entry-challan" />
        </div>
        <div>
          <label className="small-label block mb-2">Challan Series</label>
          <input className="input-lg" value={form.challan_series}
            onChange={(e) => setForm({ ...form, challan_series: e.target.value })}
            placeholder="e.g. AMB / BHW"
            data-testid="entry-challan-series" />
        </div>

        {needsParty && (
          <div>
            <label className="small-label block mb-2">{isPurchase ? "Vendor" : "Customer"}</label>
            <select className="input-lg" value={form.party_id}
              onChange={(e) => setForm({ ...form, party_id: e.target.value })}
              data-testid="entry-party" required>
              <option value="">— Select —</option>
              {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        {(needsParty || isPurchase) && !isTransfer && (
          <div>
            <label className="small-label block mb-2">{isSale ? "From Godown (our stock)" : "To Godown (our stock)"}</label>
            <select className="input-lg" value={form.from_godown}
              onChange={(e) => setForm({ ...form, from_godown: e.target.value })}
              data-testid="entry-godown" required>
              {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </div>
        )}

        {isTransfer && (
          <>
            <div>
              <label className="small-label block mb-2">From Godown</label>
              <select className="input-lg" value={form.from_godown}
                onChange={(e) => setForm({ ...form, from_godown: e.target.value })} required>
                {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className="small-label block mb-2">To Godown</label>
              <select className="input-lg" value={form.to_godown}
                onChange={(e) => setForm({ ...form, to_godown: e.target.value })} required>
                {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
              </select>
            </div>
          </>
        )}

        {isAdjust && (
          <div>
            <label className="small-label block mb-2">Godown</label>
            <select className="input-lg" value={form.godown}
              onChange={(e) => setForm({ ...form, godown: e.target.value })} required>
              {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="small-label block mb-2">Driver Name</label>
          <input className="input-lg" value={form.driver_name}
            onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
            data-testid="entry-driver" />
        </div>
        <div>
          <label className="small-label block mb-2">Vehicle No</label>
          <input className="input-lg" value={form.vehicle_no}
            onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })}
            data-testid="entry-vehicle" />
        </div>

        {isSale && (
          <div className="sm:col-span-2 lg:col-span-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-sm px-4 py-3 flex items-center gap-3">
            <input type="checkbox" id="mt_pending_toggle"
              checked={!!form.mt_pending}
              onChange={(e) => setForm({ ...form, mt_pending: e.target.checked })}
              className="w-4 h-4 accent-black"
              data-testid="mt-pending-toggle" />
            <label htmlFor="mt_pending_toggle" className="text-sm">
              <span className="font-bold text-[#B45309]">Vehicle just dispatched · MT return pending</span>
              <span className="block text-xs text-gray-600 mt-0.5">
                Record only Filled cylinders now. When vehicle returns, update MT from the
                "MT Entry Pending" page.
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="small-label text-gray-500">
            Cylinder Lines — {isPurchase ? "vendor gives Filled, we give MT" : isSale ? (form.mt_pending ? "only Filled (MT to be recorded on return)" : "we give Filled, customer gives MT") : isTransfer ? "sender gives Filled, receiver gives MT back" : "signed qty (negative to reduce)"}
          </div>
          <button type="button" onClick={() => setItems([...items, emptyItem()])}
            className="text-sm font-semibold flex items-center gap-1 hover:underline"
            data-testid="add-item-row">
            <Plus size={14} /> Add row
          </button>
        </div>
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                {type === "adjustment" && <th className="text-left">Direction</th>}
                <th className="text-left">Brand</th>
                <th className="text-left">Size</th>
                <th className="num">Filled</th>
                {!(isSale && form.mt_pending) && <th className="num">MT</th>}
                {hasMoney && <th className="num">Rate ₹{(isSale || isPurchase) ? "/kg" : ""}</th>}
                {hasMoney && <th className="num">Amount ₹</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  {type === "adjustment" && (
                    <td>
                      <select className="input-lg h-9" value={it.direction || "add"}
                        onChange={(e) => updateItem(idx, { direction: e.target.value })}
                        data-testid={`item-direction-${idx}`}>
                        <option value="add">Add (+)</option>
                        <option value="reduce">Reduce (−)</option>
                      </select>
                    </td>
                  )}
                  <td>
                    <select className="input-lg h-9" value={it.brand}
                      onChange={(e) => updateItem(idx, { brand: e.target.value })}
                      data-testid={`item-brand-${idx}`}>
                      <option value="">—</option>
                      {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="input-lg h-9" value={it.size}
                      onChange={(e) => updateItem(idx, { size: e.target.value })}
                      data-testid={`item-size-${idx}`}>
                      <option value="">—</option>
                      {sizes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="number" min={type === "adjustment" ? "0" : undefined} className="input-lg h-9 text-right data-num"
                      value={it.filled_qty}
                      onChange={(e) => updateItem(idx, { filled_qty: Number(e.target.value) })}
                      data-testid={`item-filled-${idx}`} />
                  </td>
                  {!(isSale && form.mt_pending) && (
                    <td>
                      <input type="number" min={type === "adjustment" ? "0" : undefined} className="input-lg h-9 text-right data-num"
                        value={it.mt_qty}
                        onChange={(e) => updateItem(idx, { mt_qty: Number(e.target.value) })}
                        data-testid={`item-mt-${idx}`} />
                    </td>
                  )}
                  {hasMoney && (
                    <td>
                      <input type="number" step="0.01" className="input-lg h-9 text-right data-num"
                        value={it.rate}
                        onChange={(e) => updateItem(idx, { rate: Number(e.target.value) })}
                        data-testid={`item-rate-${idx}`} />
                    </td>
                  )}
                  {hasMoney && (
                    <td>
                      <input type="number" step="0.01" className="input-lg h-9 text-right data-num"
                        value={it.amount}
                        onChange={(e) => updateItem(idx, { amount: Number(e.target.value) })}
                        data-testid={`item-amount-${idx}`} />
                    </td>
                  )}
                  <td className="text-right">
                    <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))}
                      className="text-gray-400 hover:text-[#B91C1C]"
                      data-testid={`item-delete-${idx}`}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {hasMoney && (
              <tfoot>
                <tr className="bg-gray-50">
                  <td colSpan={(isSale && form.mt_pending) ? 4 : 5} className="text-right uppercase tracking-wider text-xs text-gray-500">Cylinders subtotal</td>
                  <td className="num data-num font-bold">₹ {formatMoney(itemsTotal)}</td>
                  <td></td>
                </tr>
                {otherCharges !== 0 && (
                  <tr className="bg-gray-50">
                    <td colSpan={(isSale && form.mt_pending) ? 4 : 5} className="text-right uppercase tracking-wider text-xs text-gray-500">
                      Other charges{form.other_charges_note ? ` (${form.other_charges_note})` : ""}
                    </td>
                    <td className="num data-num font-bold text-[#B45309]">₹ {formatMoney(otherCharges)}</td>
                    <td></td>
                  </tr>
                )}
                <tr className="bg-black text-white">
                  <td colSpan={(isSale && form.mt_pending) ? 4 : 5} className="text-right font-bold uppercase tracking-wider text-xs">Grand Total</td>
                  <td className="num data-num text-lg font-bold">₹ {formatMoney(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Bottom */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {hasMoney && (
          <>
            <div>
              <label className="small-label block mb-2">Other Charges ₹</label>
              <input type="number" step="0.01" className="input-lg data-num text-right" value={form.other_charges}
                onChange={(e) => setForm({ ...form, other_charges: e.target.value })}
                placeholder="transport, hamali, GST…"
                data-testid="entry-other-charges" />
            </div>
            <div>
              <label className="small-label block mb-2">Charges Note</label>
              <input className="input-lg" value={form.other_charges_note}
                onChange={(e) => setForm({ ...form, other_charges_note: e.target.value })}
                placeholder="e.g. Transport 500 + Hamali 200"
                data-testid="entry-other-charges-note" />
            </div>
            <div>
              <label className="small-label block mb-2">Payment Received Now</label>
              <input type="number" className="input-lg" value={form.payment_received}
                onChange={(e) => setForm({ ...form, payment_received: e.target.value })}
                data-testid="entry-payment-now" />
            </div>
          </>
        )}
        <div className={hasMoney ? "" : "sm:col-span-2 lg:col-span-4"}>
          <label className="small-label block mb-2">Notes</label>
          <input className="input-lg" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="entry-notes" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
          <button className="btn-primary flex items-center gap-2 h-11 px-6" disabled={busy}
            data-testid="entry-submit">
            <Save size={16} /> {busy ? "Saving…" : "Save Entry"}
          </button>
        </div>
      </div>
    </form>
  );
}
